import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";

import photo_default from "../../assets/images/empty_photo.jpeg";
import { Button, Label, TextError, TextPlain, Title, TitleH2, } from "components/generic";
import { IPerson, IPropsFC, RoleUser, TExperience, TGender, } from "../../interfaces/intarfaces";

import { ContainerRedirect, ContainerValue, ContainerView, DialogWrapper, FlexBox, } from "components/containers";
import { useInput } from "components/Input";
import { useCalendar } from "components/Calendar";
import { useTextArea } from "components/TextArea";
import { useRange } from "components/Range";
import { useSelect } from "components/Select";
import { useCheckbox, useRadiobox } from "components/CheckRadiobox";
import { getLanguage } from "utils/fetchMapInfo";
import { useMultiSelect } from "components/MultiSelect";
import { UserAPI } from "API/userApi";

import { useContextHospi } from "context/ContextHospi";
import { API_USERS, HOST_PROFILEVIEW_ROUTE, STUDENT_PROFILEVIEW_ROUTE, HOST_ROOMS_ROUTE, STUDENT_ROOMS_ROUTE} from "utils/constants";
import { Comparator } from "utils/logics";
import { SocialPreferences, TitleProfile, } from "pages/ProfileUser/SimpleComponents";
import { MenuLeftSide } from "components/MenuLeftSide";
import { PersonalDetailsEdit } from "pages/ProfileUser/PersonalDetailsEdit";
import { MainSide } from "./styles";

import { SearchCriteria } from "pages/ProfileUser/SearchCriteria";
import { AboutMeComponent } from "pages/ProfileUser/AboutMeComponent";
import Modal from "components/Modal";

import useSizeWindow from "utils/hooks/useWindowSize";
import { isoDate } from "utils/helpers";
import { LoaderCustom } from "components/Loader";
import { theme } from "styles/theme";
import { uploadAvatar } from "utils/uploadAvatar";
import { TLang } from "assets/data/isoNameLanguage";

// Max width Container
const maxWidthContainer = "800px";

export const ProfileUserEdit: React.FC = () => {
    const navigate = useNavigate();
    const [showModal, setShowModal] = useState(0);
    const [deleteUserAccount, setDeleteUserAccount] = useState(false);

    const { userProfile, updateProfile, userStatus, removeUserAccount, setUser } =
        useContextHospi();
    const [currentUserID, setCurrentUserID] = useState(
        userProfile?.user_id || ""
    );
    const user: RoleUser = userStatus || "student";
    const { t, i18n } = useTranslation(user);
    const { t: t_common } = useTranslation();
    const device = useSizeWindow();
    const [disabledSendButton, setDisabledSendButton] = useState(
        user === "student"
    );
    const [responseError, setResponseError] = useState("");
    const [loading, setLoading] = useState(false);
    const [textLoader, setTextLoader] = useState("");
    const loadersTexts = {
        loadingData: t("textLoading.0"),
        savingData: t("textLoading.1"),
    };

    useEffect(() => {
        const getID = async () => {
            const { attributes } = await Auth.currentAuthenticatedUser();
            setCurrentUserID(attributes.sub ?? "");
        };
        getID();
    }, [userProfile]);

    const checkItemExistsInArray = (item: TExperience): boolean | undefined =>
        userProfile &&
        userProfile?.past_experience &&
        userProfile?.past_experience.indexOf(item) !== -1;

    const titlePholder = (part: string, variable: string): IPropsFC => ({
        title: t(`${user}Profile.${part}.form.${variable}.title`),
        placeholder: t(`${user}Profile.${part}.form.${variable}.placeholder`),
        w: "initial",
        grow: "1",
    });

    const firstName = {
        ...useInput(userProfile?.first_name || ""),
        name: "firstname",
        required: true,
    };
    const lastName = {
        ...useInput(userProfile?.last_name || ""),
        name: "lastname",
        required: true,
    };
    const dateBirth = {
        ...useInput(
            userProfile?.birth_date ? isoDate(new Date(userProfile?.birth_date)) : ""
        ),
        name: "dateofbirth",
        required: true,
    };
    const birthCountry = {
        ...useSelect(userProfile?.birth_country || ""),
        required: true,
    };
    const phoneCode = {
        ...useSelect(userProfile?.phone_code || undefined),
        required: true,
    };
    const phone = {
        ...useInput(userProfile?.phone_number || "", 15),
        name: "tel",
        required: true,
    };

    const dateIn = {
        ...useCalendar({
            value: userProfile?.preferred_move_in
                ? new Date(userProfile?.preferred_move_in)
                : undefined,
            range: false,
        }),
    };

    const dateOut = {
        ...useCalendar({
            value: userProfile?.preferred_move_out
                ? new Date(userProfile?.preferred_move_out)
                : undefined,
            range: false,
        }),
    };

    const budget = {
        ...useInput(userProfile?.max_budget || ""),
        required: user === "student",
    };

    const countryResidence = useSelect("NL");

    const stateResidence = {
        ...useSelect(userProfile?.city_of_residence?.province || undefined),
        required: true,
    };

    const cityResidence = {
        ...useSelect(userProfile?.city_of_residence?.city || undefined),
        required: true,
    };

    const gender = {
        ...useRadiobox("gender", userProfile?.gender || ""),
        name: "gender",
    };

    const study = {
        ...useInput(userProfile?.study || ""),
        ...(user === "student" ? titlePholder("aboutMeInfo", "study") : {}),
        required: user === "student",
    };
    const spokenLanguage = {
        ...useMultiSelect(
            getLanguage(i18n.language.toUpperCase() as TLang),
            userProfile?.spoken_languages || [],
            ["gb", "nl"]
        ),
        ...titlePholder("aboutMeInfo", "spokenLanguage"),
        required: true,
    };
    const livedWithHost = useCheckbox(checkItemExistsInArray("Lived with host"));
    const livedAbroad = useCheckbox(checkItemExistsInArray("Lived abroad"));
    const livedOwn = useCheckbox(checkItemExistsInArray("Lived on my own"));
    const livedAny = useCheckbox(checkItemExistsInArray("None of the above"));
    const shortDescription = {
        ...useTextArea(userProfile?.description || ""),
        ...titlePholder("aboutMeInfo", "aboutYourself"),
        required: user === "student",
    };

    const privacy = useRange(userProfile?.privacy || 50);
    const newInTown = useRange(
        (user === "host"
            ? userProfile?.requested_autonomy
            : userProfile?.autonomy) || 50
    );

    // Edited Photo
    const [readyPhoto, setReadyPhoto] = useState<File | undefined>(undefined);

    const yearFirstBiggerThan = !!(!!(dateBirth.value as string).match(/^(\d{4})[-./](\d{2})[-./](\d{2})$/)
        && dateBirth.value)
        && +(dateBirth.value as string).slice(0, 4) >= 1900
        && +(dateBirth.value as string).slice(5, 7) <= 12
    const yearLastBiggerThan = !!(!!(dateBirth.value as string).match(/^(\d{2})[-./](\d{2})[-./](\d{4})$/)
        && dateBirth.value)
        && +(dateBirth.value as string).slice(6) >= 1900
        && +(dateBirth.value as string).slice(3, 5) <= 12

    React.useEffect(() => {
        console.log(birthCountry.value)
        const hostValidate = String(firstName.value).length > 0 &&
            String(lastName.value).length > 0 && phoneCode.value !== undefined && phoneCode.value !== '' &&
            String(phone.value).length > 0 && gender.selected.length > 0 && spokenLanguage.values.length > 0
        const studentValidate = hostValidate &&
            dateBirth.value !== undefined &&
            (yearFirstBiggerThan || yearLastBiggerThan) &&
            birthCountry.value !== undefined &&
            birthCountry.value !== '' &&
            dateIn.value !== undefined &&
            dateOut.value !== undefined &&
            String(budget.value).length > 0 &&
            countryResidence.value !== undefined &&
            stateResidence.value !== undefined &&
            cityResidence.value !== undefined &&
            cityResidence.value !== '' &&
            String(study.value).length > 0

        const checkFillAllFields = (isFilled: boolean) => setDisabledSendButton(!isFilled)

        checkFillAllFields(user === "student" ? studentValidate : hostValidate);
    });


    const sendFromData = async () => {
        //@ts-ignore
        const isSafari = /constructor/i.test(window.HTMLElement) || (function (p) { return p.toString() === "[object SafariRemoteNotification]"; })(!window['safari'] || (typeof safari !== 'undefined' && safari.pushNotification));
        setTextLoader(loadersTexts.savingData);
        setLoading(true);
        const formPastExperience: TExperience[] = [];

        livedWithHost.checked && formPastExperience.push("Lived with host");
        livedAbroad.checked && formPastExperience.push("Lived abroad");
        livedOwn.checked && formPastExperience.push("Lived on my own");
        livedAny.checked && formPastExperience.push("None of the above");

        const userFormData: Partial<IPerson> = {
            user_id: currentUserID,
            first_name: firstName.value as string,
            last_name: lastName.value as string,
            phone_code: phoneCode.value as string,
            phone_number: phone.value as string,
            gender: gender.selected as TGender,
            spoken_languages: spokenLanguage.values,
            default_language: i18n.language,
            description: shortDescription.value as string,
            privacy: privacy.value,
        };

        let safariBirthday;
        safariBirthday =
            (dateBirth.value as string).replace(/[-/.]/g, '-').split('-');

        safariBirthday = safariBirthday[0].length === 4 ? safariBirthday.join('-') : safariBirthday[2] + '-' + safariBirthday[1] + '-' + safariBirthday[0]

        const studentFormData: Partial<IPerson> = {
            birth_date: (isSafari ? safariBirthday : (dateBirth.value as string)) ?? "",
            birth_country: birthCountry.value as string,
            preferred_move_in: isoDate(dateIn.value),
            preferred_move_out: isoDate(dateOut.value),
            max_budget: (budget.value || 0) as number,
            city_of_residence: {
                country: (countryResidence.value as string) || "",
                province: (stateResidence.value as string) ?? "",
                city: (cityResidence.value as string) ?? "",
            },
            study: study.value as string,
            past_experience: formPastExperience,
            autonomy: newInTown.value,
            user_type: "student",
        };

        const hostFormData: Partial<IPerson> = {
            requested_autonomy: newInTown.value,
            user_type: "host",
        };

        const data =
            user === "host"
                ? Comparator(
                    { ...userFormData, ...hostFormData },
                    userProfile || {},
                    user
                )
                : Comparator(
                    { ...userFormData, ...studentFormData },
                    userProfile || {},
                    user
                );
        let response;
        if (Object.keys(data).length > 0) {
            response = await UserAPI.update(API_USERS + `${user}/${currentUserID}`, {
                ...data,
                user_type: user,
            });
            if (response.hasOwnProperty("status")) {
                setResponseError(
                    response.errorMessages
                        .map((line: [string, string]) => `${line[0]} - ${line[1]}`)
                        .join("\n")
                );
                setLoading(false);
                return;
            }
        }
        const [thumbnail_url, is_profile_complete] = await uploadAvatar(
            readyPhoto,
            currentUserID
        );
        if (thumbnail_url !== 'invalid_type') {
            updateProfile({
                ...(response ? (response as Partial<IPerson>) : userProfile),
                ...(readyPhoto
                    ? {
                        thumbnail_url: !!thumbnail_url.length ? thumbnail_url : undefined,
                        is_profile_complete: is_profile_complete ?? false,
                    }
                    : {}),
            });
            navigate(
                user === "host" ? `/${i18n.language}${HOST_PROFILEVIEW_ROUTE.slice(5)}` : `/${i18n.language}${STUDENT_PROFILEVIEW_ROUTE.slice(5)}`
            );
        } else if (thumbnail_url === 'invalid_type') {
            setResponseError(t_common('fileTypeError'))
        }
        setLoading(false);
    };

    const skipProfile = async () => {
        navigate(
            user === "host" ? `/${i18n.language}${HOST_PROFILEVIEW_ROUTE.slice(5)}` : `/${i18n.language}${STUDENT_PROFILEVIEW_ROUTE.slice(5)}`
        );
    };

    const toRooms = async () => {
        navigate(
            user === "host" ? `/${i18n.language}${HOST_ROOMS_ROUTE.slice(5)}` : `/${i18n.language}${STUDENT_ROOMS_ROUTE.slice(5)}`
        );
    };

    // Delete User
    const deleteUser = async () => {
        setLoading(true);
        setShowModal(0);
        const response = await UserAPI.delete(API_USERS + `${currentUserID}`);
        setLoading(false);
        if (response.hasOwnProperty("success") && response["success"]) {
            setUser(undefined);
            setDeleteUserAccount(true);
            navigate(`/${i18n.language}/`);
        }
        navigate(`/${i18n.language}/`);
    };

    if (deleteUserAccount)
        return (
            <ContainerView>
                <MainSide>
                    <FlexBox direction="column">
                        <ContainerRedirect>
                            <Title m="0 0 24px 0">{t("deleteAccount.title")}</Title>
                            <Label lineHeight="150%" p="0">
                                {t("deleteAccount.text")}
                            </Label>
                        </ContainerRedirect>
                        <Button
                            main
                            fontWeight="400"
                            w="100%"
                            m="24px 0 0 0"
                            onClick={() => removeUserAccount()}
                            style={{ maxWidth: "336px" }}
                        >
                            {t("deleteAccount.button")}
                        </Button>
                    </FlexBox>
                </MainSide>
            </ContainerView>
        );

    return (
        <ContainerView>
            <MenuLeftSide
                routeActive={
                    user === "host" ? `/${i18n.language}${HOST_PROFILEVIEW_ROUTE.slice(5)}` : `/${i18n.language}${STUDENT_PROFILEVIEW_ROUTE.slice(5)}`
                }
            />
            <MainSide>
                {loading && (
                    <LoaderCustom
                        size={5}
                        color={theme.colors.bgBody}
                        text={textLoader}
                    />
                )}
                <TitleProfile user={userStatus!} title={t(`viewTitle`)} edit="true" />
                <ContainerValue maxWidth={maxWidthContainer} id="personalDetails">
                    <Title m="0 0 24px 0" fontSize="20px">
                        {t(`${user}Profile.personalDetails.title`)}
                    </Title>
                    <PersonalDetailsEdit
                        firstName={firstName}
                        lastName={lastName}
                        dateBirth={dateBirth}
                        birthCountry={birthCountry}
                        phoneCode={phoneCode}
                        phone={phone}
                        photo={
                            `${userProfile?.thumbnail_url}?${new Date().toString()}` ||
                            photo_default
                        }
                        readyPhoto={setReadyPhoto}
                    />
                </ContainerValue>
                {userStatus === "student" && (

                    <ContainerValue maxWidth={maxWidthContainer} id="searchCriteria">

                        <Title m="0 0 24px 0" fontSize="20px">
                            {t(`${user}Profile.searchCriteria.title`)}
                        </Title>
                        <SearchCriteria
                            dateIn={dateIn}
                            dateOut={dateOut}
                            budget={budget}
                            countryResidence={countryResidence}
                            provinceResidence={stateResidence}
                            cityResidence={cityResidence}
                        />
                    </ContainerValue>
                )}
                <ContainerValue maxWidth={maxWidthContainer} id="aboutMeInfo">
                    <Title m="0 0 24px 0" fontSize="20px">
                        {t(`${user}Profile.aboutMeInfo.title`)}
                    </Title>
                    <AboutMeComponent
                        gender={gender}
                        study={study}
                        spokenLanguage={spokenLanguage}
                        expirience={[livedWithHost, livedAbroad, livedOwn, livedAny]}
                        shortDescription={shortDescription}
                    />
                </ContainerValue>
                <ContainerValue maxWidth={maxWidthContainer} id="socialPreferences">
                    <Title m="0 0 24px 0" fontSize="20px">
                        {t(`${user}Profile.socialPreferences.title`)}
                    </Title>
                    <SocialPreferences
                        roleUser={userStatus || "student"}
                        privacy={privacy}
                        newInTown={newInTown}
                        muted={false}
                    />
                </ContainerValue>
                <div style={{ width: "100%", maxWidth: maxWidthContainer }}>
                    <TextError>{responseError}</TextError>
                    <FlexBox
                        justify="space-between"
                        margin="0 0 24px 0"
                        width="100%"
                        className="button-group"
                    >
                        <FlexBox justify="flex-start" margin="24px 0">
                            <Button outline onClick={() => setShowModal(1)}>
                                {device === "mobile"
                                    ? "Delete"
                                    : t(`${user}Profile.buttonRemove.title`)}
                            </Button>
                        </FlexBox>
                        <FlexBox justify="flex-end" margin="24px 0">
                            <Button normal onClick={() => skipProfile()}>
                                {t(`${user}Profile.buttonCancel.title`)}
                            </Button>
                            <Button
                                main
                                onClick={() => {sendFromData().then(() => toRooms())}}
                                m="0 0 0 24px"
                                disabled={disabledSendButton}
                            >
                                {t(`${user}Profile.buttonSaveContinue.title`)}
                            </Button>
                        </FlexBox>
                    </FlexBox>
                </div>

                {showModal !== 0 && (
                    <Modal isShow={!!showModal} handleClose={() => setShowModal(0)}>
                        <DialogWrapper>
                            <TitleH2>{t(`${user}Profile.deleteAccount.title`)}</TitleH2>
                            <TextPlain
                                style={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
                            >
                                {t(`${user}Profile.deleteAccount.text`, {
                                    joinArrays: "\n",
                                })}
                            </TextPlain>
                            <div className="buttons">
                                <Button normal onClick={() => setShowModal(0)}>
                                    {t(`${user}Profile.deleteAccount.buttonKeep`)}
                                </Button>
                                <Button danger onClick={deleteUser}>
                                    {t(`${user}Profile.deleteAccount.buttonDelete`)}
                                </Button>
                            </div>
                        </DialogWrapper>
                    </Modal>
                )}
            </MainSide>
        </ContainerView>
    );
};
